import React, { useEffect, useState } from 'react';

import styles from './Parts.module.css';
import { Button } from "../../ui/General/LucideButton/LucideButton.tsx";
import { Checkbox } from "../../ui/General/LucideCheckbox/LucideCheckbox.tsx";
import { Input } from "../../ui/General/LucideInput/LucideInput.tsx";
import { Filter, Search } from "lucide-react";
import { SearchProductList } from "./components/SearchProductList/SearchProductList.tsx";
import { Product } from "../../widgets/ProductCard/types/productCard.interface.ts";
import { SearchCarPartsWidget } from "../../widgets/SearchCarPartsWidget/SearchCarPartsWidget.tsx";
import { useParams } from "react-router-dom";
import { useScreenDetector } from '../../hooks/useScreenDetector.ts';
import Preloader from '../../ui/Loader/Preloader.tsx';
import { cn } from '../../utils/cn.ts';
import { SelectOption } from '../../ui/SelectFiltered/types/SelectOption.interface.ts';
import { API_URL } from '../../config.tsx';

export const PartPage: React.FC = () => {
    const {
        categorySlug,
        brandSlug,
        modelSlug,
        bodyNumberOrEngineNumber,
    } = useParams<{ categorySlug: string, brandSlug: string, modelSlug: string, bodyNumberOrEngineNumber?: string }>();
    const [stateBrandSlug, setStateBrandSlug] = useState<string>(brandSlug || '');
    const [stateModelSlug, setStateModelSlug] = useState<string>(modelSlug || '');
    const [vinCarNumber, setVinCarNumber] = useState<string | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setIsLoading] = useState<boolean>(true);
    const [emptyProducts, setEmptyProducts] = useState<boolean>(false);
    const [filteredBrands, setFilteredBrands] = useState<{ id: number, name: string }[]>([]);
    const [checkedBrandsIds, setCheckedBrandsIds] = useState<number[]>([]);
    const [brandSearchQuery, setBrandSearchQuery] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [showAllBrands, setShowAllBrands] = useState(false);
    const { isMobile } = useScreenDetector()
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [init, setInit] = useState(true);
    const [brandCode, setBrandCode] = useState<string>('');
    const [ssd, setSsd] = useState<string>('');
    const [vinOrCarNumber, setVinOrCarNumber] = useState<string>(vinCarNumber || '');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [step, setStep] = useState<number>(1);
    const [stepValues, setStepValues] = useState<{ [key in number]: string }>({});
    const [catalogs, setCatalogs] = useState<{ [key in number]: { name: string, options: SelectOption[] } }>({});

    const search = () => {
        if (vinOrCarNumber) {
            findProductsByLaximo(vinOrCarNumber, null, null, searchQuery);
        } else {
            findProductsByLaximo(null, brandCode, ssd, searchQuery);
        }
    }
    const findProducts = () => {
        fetch(`${API_URL}/find_public_parts?categorySlug=${categorySlug || ''}&brandSlug=${brandSlug || ''}&modelSlug=${modelSlug || ''}&bodyNumberOrEngineNumber=${bodyNumberOrEngineNumber || ''}`, {
            method: 'GET',
            credentials: 'include',
        })
            .then(response => response.json())
            .then(result => {
                setData(result);
            })
            .then(() => setIsLoading(false))
    };

    const findProductsByLaximo = (vinOrCarNumber: string | null, brandCode: string | null, ssd: string | null, partName: string) => {
        setIsLoading(true);
        setVinCarNumber(vinOrCarNumber);
        setCategoryName(partName);
        fetch(`${API_URL}/find_public_parts_by_car_data?vinOrCarNumber=${vinOrCarNumber || ''}&brandCode=${brandCode || ''}&ssd=${ssd || ''}&partName=${partName}`, {
            method: 'GET',
            credentials: 'include',
        })
            .then(response => response.json())
            .then(result => {
                setData(result);
            }).then(() => setIsLoading(false))
    };

    const setData = (result: any) => {
        if (Object.keys(result).length === 0) {
            setEmptyProducts(true);
            setIsLoading(false);
            return;
        }
        setCategoryName(result.categoryName);
        setSearchQuery(result.categoryName);
        setStateBrandSlug(result.brand);
        setStateModelSlug(result.model);

        if (Object.keys(result.products).length === 0) {
            setEmptyProducts(true);
            setIsLoading(false);
            return;
        }

        let filteredBrands: { id: number, name: string }[] = [];

        result.products.map((product: Product) => {
            if (!filteredBrands.find(brand => brand.id === product.brandId)) {
                filteredBrands.push({ id: product.brandId, name: product.brand });
            }
        })
        setEmptyProducts(false);
        setProducts(result.products);
        setFilteredBrands(filteredBrands);
    }

    const filteredProducts = (products: Product[], filteredBrands: number[]) => {
        if (filteredBrands.length === 0) return products;
        return products.filter(product => filteredBrands.includes(product.brandId));
    }

    const toggleChangeFilterBrand = (value: number) => {
        setCheckedBrandsIds(checkedBrandsIds.includes(value) ? checkedBrandsIds.filter(id => id !== value) : [...checkedBrandsIds, value]);
    }

    useEffect(() => {
        findProducts();
    }, []);

    if (loading) return <Preloader />;

    return (
        <div className="container mx-auto flex-1 py-8 px-4 md:px-6">
            <div className="container mx-auto px-4 py-6">
                <SearchCarPartsWidget
                    brand={stateBrandSlug || ''}
                    modelSlug={stateModelSlug || ''}
                    bodySlug={bodyNumberOrEngineNumber || ''}
                    categoryName={categoryName}
                    init={init}
                    setInit={setInit}
                    brandCode={brandCode}
                    setBrandCode={setBrandCode}
                    setSsd={setSsd}
                    vinOrCarNumber={vinOrCarNumber}
                    setVinOrCarNumber={setVinOrCarNumber}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    step={step}
                    setStep={setStep}
                    stepValues={stepValues}
                    setStepValues={setStepValues}
                    catalogs={catalogs}
                    setCatalogs={setCatalogs}
                    search={search}
                />
                {
                    emptyProducts
                        ? <h3 className={styles.emptyProducts}>Ничего не найдено. Попробуйте воспользоваться поиском по Госномеру или по параметрам автомобиля</h3>
                        :
                        <div className={styles.mainContent}>
                            <button
                                className={styles.mobileFilterButton}
                                onClick={() => setShowMobileFilters(!showMobileFilters)}
                            >
                                <Filter className={styles.mobileFilterIcon} />
                                Фильтры
                            </button>
                            <div className={cn(styles.sidebar, `${isMobile ? (showMobileFilters ? 'block' : 'hidden lg:block') : ''}`)
                            }>
                                <div className={styles.filterCard}>
                                    <h3 className={styles.filterTitle}>
                                        <span className="mr-2">▼</span> БРЕНД
                                    </h3>

                                    <div className={styles.brandSearch}>
                                        <Search className={styles.brandSearchIcon} />
                                        <Input
                                            placeholder="Поиск"
                                            className={styles.brandSearchInput}
                                            type="text"
                                            value={brandSearchQuery}
                                            style={{ paddingTop: 0, paddingBottom: 0, width: 'auto', paddingLeft: 34, }}
                                            onChange={(e) => setBrandSearchQuery(e.target.value)}
                                        />
                                    </div>

                                    <div className={styles.brandList}>
                                        {filteredBrands.filter(brand =>
                                            brand.name.toLowerCase().includes(brandSearchQuery.toLowerCase())
                                        ).slice(0, showAllBrands ? undefined : 3).map((brand) => (
                                            <div key={brand.id} className={styles.brandItem}>
                                                <Checkbox
                                                    id={brand.id.toString()}
                                                    checked={checkedBrandsIds.includes(brand.id)}
                                                    onCheckedChange={() => toggleChangeFilterBrand(brand.id)}
                                                />
                                                <label htmlFor={brand.id.toString()} className="text-sm">{brand.name}</label>
                                            </div>
                                        ))}
                                    </div>

                                    {filteredBrands.filter(({ name }) =>
                                        name.toLowerCase().includes(brandSearchQuery.toLowerCase())
                                    ).length > 3 && (
                                            <button
                                                onClick={() => setShowAllBrands(prev => !prev)}
                                                className="text-sm text-blue-600 mt-3"
                                            >
                                                {showAllBrands ? 'Свернуть' : 'Показать еще ' + (filteredBrands.filter(brand => brand.name.toLowerCase().includes(brandSearchQuery.toLowerCase())).length - 3)}
                                            </button>
                                        )}

                                    <div className="mt-4 mbflex flex-col space-y-2">
                                        <Button variant="outline" size="sm" className="text-[#ea8d00] hover:bg-[#d47b00] hover:text-white" onClick={() => setCheckedBrandsIds([])}>Сбросить</Button>
                                    </div>
                                </div>
                            </div>
                            <SearchProductList querySearch={''} isLoading={loading} products={filteredProducts(products, checkedBrandsIds)} />
                        </div>
                }
            </div>
        </div>
    );
};
